import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Services.css';

gsap.registerPlugin(ScrollTrigger);

/** Largeur max (px) : une carte = toute la largeur utile (alignée sur le scroll horizontal GSAP). */
const SRV_FULL_BLEED_MAX_PX = 1024;

function syncSrvCardWidths(trackEl) {
  const w = window.innerWidth;
  const fullBleed = w <= SRV_FULL_BLEED_MAX_PX;
  trackEl.querySelectorAll('.srv-card').forEach((card) => {
    if (fullBleed) {
      card.style.width = '100vw';
      card.style.minWidth = '100vw';
      card.style.flex = '0 0 100vw';
    } else {
      card.style.removeProperty('width');
      card.style.removeProperty('min-width');
      card.style.removeProperty('flex');
    }
  });
}

const services = [
  {
    title: 'Pharmacie & Parapharmacie',
    subtitle: 'Aménagement',
    img: '/images/pharmacie.webp',
    accent: 'Précision.',
  },
  {
    title: 'Restaurant & Café',
    subtitle: 'Aménagement',
    img: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&q=80&w=900',
    accent: 'Atmosphère.',
  },
  {
    title: 'Bureaux Professionnels',
    subtitle: 'Aménagement',
    img: '/images/bureaux.webp',
    accent: 'Performance.',
  },
  {
    title: 'Villas & Appartements',
    subtitle: 'Aménagement',
    img: '/images/villas-prestigia.webp',
    accent: 'Élégance.',
  },
  {
    title: 'Plans d\'Exécution',
    subtitle: 'Conception &',
    img: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=80&w=900',
    accent: 'Vision.',
  },
  {
    title: 'Salon de Coiffure',
    subtitle: 'Décoration',
    img: '/images/salon.webp',
    accent: 'Style.',
  },
];

export default function Services() {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const headingRef = useRef(null);
  const cardsRef = useRef([]);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    const cards = cardsRef.current;
    if (!section || !track) return;

    syncSrvCardWidths(track);

    const horizontalScrollDistance = () =>
      Math.max(0, track.scrollWidth - window.innerWidth);

    const ctx = gsap.context(() => {

    gsap.to(track, {
      x: () => -horizontalScrollDistance(),
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: () => `+=${horizontalScrollDistance()}`,
        pin: true,
        scrub: 1,
        snap: window.innerWidth <= SRV_FULL_BLEED_MAX_PX ? 1 / services.length : null,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    });

    // Stagger card entrance animations
    cards.forEach((card, i) => {
      if (!card) return;

      // Image parallax inside each card
      const img = card.querySelector('.srv-card__img');
      gsap.fromTo(img, 
        { scale: 1.3 },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: card,
            containerAnimation: gsap.getById?.('horizontalScroll'),
            start: 'left right',
            end: 'right left',
            scrub: 1,
          },
        }
      );

      // Gold accent line drawing
      const line = card.querySelector('.srv-card__accent-line');
      gsap.fromTo(line,
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 1.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    });

    // Heading reveal animation
    gsap.fromTo(headingRef.current,
      { y: 80, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1.4,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      }
    );

    }, section); // end gsap.context

    const refreshLayout = () => {
      syncSrvCardWidths(track);
      ScrollTrigger.refresh();
    };

    let resizeRaf = 0;
    const onResize = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(refreshLayout);
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', refreshLayout);
    window.addEventListener('load', refreshLayout);

    requestAnimationFrame(refreshLayout);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', refreshLayout);
      window.removeEventListener('load', refreshLayout);
      cancelAnimationFrame(resizeRaf);
      ctx.revert();
    };
  }, []);

  return (
    <section className="srv-section" id="services" ref={sectionRef}>
      {/* Sticky Header visible during horizontal scroll */}
      <div className="srv-header" ref={headingRef}>
        <span className="visually-hidden" aria-hidden="true">Nos Services de Menuiserie et Aménagement Intérieur Sur Mesure</span>
        <span className="srv-header__label">Nos Savoir-Faire</span>
        <h2 className="srv-header__title">
          L'ART DE<br />
          <em>LA MATIÈRE</em>
        </h2>
      </div>

      {/* Horizontal scroll track */}
      <div className="srv-track" ref={trackRef}>
        {services.map((srv, i) => (
          <div
            key={i}
            className="srv-card"
            ref={el => (cardsRef.current[i] = el)}
          >
            {/* Background image with parallax */}
            <div className="srv-card__img-wrap">
              <img
                src={srv.img}
                alt={srv.title}
                className="srv-card__img"
                loading="lazy"
              />
              <div className="srv-card__overlay" />
            </div>

            {/* Card content */}
            <div className="srv-card__content">
              <span className="srv-card__number">0{i + 1}</span>
              <div className="srv-card__text">
                <span className="srv-card__subtitle">{srv.subtitle}</span>
                <h3 className="srv-card__title">{srv.title}</h3>
                <div className="srv-card__accent-line" />
              </div>
              <span className="srv-card__accent-word">{srv.accent}</span>
            </div>
          </div>
        ))}

        {/* Final CTA card */}
        <div className="srv-card srv-card--cta">
          <div className="srv-card--cta__inner">
            <h3 className="srv-card--cta__title">Votre projet<br/>mérite l'exception.</h3>
            <p className="srv-card--cta__desc">Parlez-nous de votre vision.<br/>Nous la rendons réelle.</p>
            <a
              href="https://wa.me/212661338406?text=Bonjour%20Luxury%20Wood%20Concept%2C%20je%20voudrais%20discuter%20d%27un%20projet."
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary srv-cta-btn"
            >
              Étude Projet Rapide →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
